import { Head, Link, router } from "@inertiajs/react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useEffect, useState } from "react";
import { CATEGORY_OPTIONS } from "@/lib/constants";

const STATUS_LABELS = {
  validated: "Produits validés",
  ready_for_analysis: "Produits prêts pour analyse",
  analyzing: "Produits en cours d'analyse",
  analysis_error: "Produits en erreur",
  pending: "Produits en attente",
};

const STATUS_BADGE_LABELS = {
  validated: "Validé",
  ready_for_analysis: "Prêt pour analyse",
  analyzing: "En cours d'analyse",
  analysis_error: "Erreur",
  pending: "En attente d'information",
};

const STATUS_COLORS = {
  validated: "bg-emerald-100 text-emerald-700 hover:bg-emerald-100",
  ready_for_analysis: "bg-blue-100 text-blue-700 hover:bg-blue-100",
  analyzing: "bg-purple-100 text-purple-700 hover:bg-purple-100",
  analysis_error: "bg-red-100 text-red-700 hover:bg-red-100",
  pending: "bg-amber-100 text-amber-700 hover:bg-amber-100",
};

export default function ProductsIndex({ products, status, category }) {
  const title = STATUS_LABELS[status] || "Tous les produits";
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const isReadyForAnalysis = status === "ready_for_analysis";
  const readyCount = products.filter((p) => p.status === "ready_for_analysis").length;

  useEffect(() => {
    if (!isReadyForAnalysis) return;

    const interval = setInterval(() => {
      setIsRefreshing(true);
      router.reload({
        only: ["products"],
        onFinish: () => setIsRefreshing(false),
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [isReadyForAnalysis]);

  function handleRowClick(product) {
    router.visit(`/admin/products/${product.id}/edit`);
  }

  function handleAnalyzeAll() {
    setIsAnalyzing(true);
    router.post(
      "/admin/products/analyze_all",
      { category },
      {
        onFinish: () => setIsAnalyzing(false),
      },
    );
  }

  function handleCategoryChange(newCategory) {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (newCategory && newCategory !== "all") params.set("category", newCategory);
    router.visit(`/admin/products${params.toString() ? `?${params.toString()}` : ""}`);
  }

  return (
    <div className="p-6">
      <Head title={title} />
      <div className="flex items-center gap-4 mb-6">
        <Link href="/admin" className="text-blue-600 hover:underline">
          ← Retour
        </Link>
        <h1 className="text-2xl font-bold">{title}</h1>
        <Select value={category || "all"} onValueChange={handleCategoryChange}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Toutes les catégories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les catégories</SelectItem>
            {CATEGORY_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isReadyForAnalysis && (
          <>
            {isRefreshing && (
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            )}
            {readyCount > 0 && (
              <Button onClick={handleAnalyzeAll} disabled={isAnalyzing} className="ml-auto">
                {isAnalyzing ? "Lancement..." : `Analyser tous (${readyCount})`}
              </Button>
            )}
          </>
        )}
      </div>

      {products.length === 0 ? (
        <p className="text-gray-500">Aucun produit trouvé.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Marque</TableHead>
              <TableHead>Nom</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Date de création</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.id} onClick={() => handleRowClick(product)} className="cursor-pointer">
                <TableCell>{product.brand || "—"}</TableCell>
                <TableCell>{product.name || "—"}</TableCell>
                <TableCell>
                  <Badge className={STATUS_COLORS[product.status]} title={product.analysis_error || undefined}>
                    {STATUS_BADGE_LABELS[product.status]}
                  </Badge>
                </TableCell>
                <TableCell>{product.created_at}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
